import { useCurrentUser } from 'croods-auth'
import { useWindowDimensions } from '@seasonedsoftware/utils'
import get from 'lodash/get'

import { useHasAnyPurchasedGuide } from 'utils/usePurchasedGuides'
import { useSignOutHandler } from 'auth/handleSignOut'
import useLogEvent from 'amplitude/useLogEvent'
import {
  CLICK_TO_SUBSCRIBER_PAGE_EVENT,
  CLICK_SUBSCRIPTION_ACCOUNT_MENU_EVENT,
  CLICK_HELP_ACCOUNT_MENU_EVENT,
  CLICK_REQUEST_FEATURE_ACCOUNT_MENU_EVENT,
} from 'amplitude/events'
import { useHasSubscriberPrivilege } from 'utils/useUserType'
import useFreemiumExperiment from 'experiments/useFreemiumExperiment'

const adminOrOperator = userType =>
  userType === 'admin' || userType === 'operator'

const useLinksToNonSignedUsers = () => {
  const { hasFreemium } = useFreemiumExperiment()
  const logEvent = useLogEvent()
  const logIn = {
    name: 'Log In',
    variant: 'outlined',
    path: '/sign-in',
  }
  const getStarted = {
    name: hasFreemium ? 'Sign Up' : 'Get Started',
    variant: 'contained',
    path: '/subscribe',
    onClick: () => {
      logEvent(CLICK_TO_SUBSCRIBER_PAGE_EVENT, {
        'Button Type': 'Header Button',
      })
    },
  }

  return [logIn, getStarted]
}

const useSignedInLinks = () => {
  const [{ currentUser }] = useCurrentUser()
  const hasSubscriberPrivilege = useHasSubscriberPrivilege()
  const handleSignOut = useSignOutHandler()
  const userType = get(currentUser, 'userType')
  const hasAnyPurchasedGuide = useHasAnyPurchasedGuide()

  const logEvent = useLogEvent()

  if (!currentUser) return []

  const cmsOption = adminOrOperator(userType)
    ? { name: 'CMS', path: '/admin' }
    : null

  const subscriptionOption = hasSubscriberPrivilege
    ? {
        name: 'Subscription',
        path: '/subscription',
        onClick: () => {
          logEvent(CLICK_SUBSCRIPTION_ACCOUNT_MENU_EVENT)
        },
      }
    : null

  const purchasedGuidesOption = hasAnyPurchasedGuide
    ? {
        name: 'Purchased Guides',
        path: '/purchased-guides',
      }
    : null

  const helpOption = {
    name: 'Help',
    path: process.env.REACT_APP_HELPCENTER_HOME,
    onClick: () => {
      logEvent(CLICK_HELP_ACCOUNT_MENU_EVENT)
    },
  }

  const requestFeatureOption = {
    name: 'Request a feature',
    path: process.env.REACT_APP_HELPCENTER_NEW_TICKET,
    onClick: () => {
      logEvent(CLICK_REQUEST_FEATURE_ACCOUNT_MENU_EVENT)
    },
  }

  return [
    {
      name: 'Account',
      links: [
        cmsOption,
        { name: 'Edit Profile', path: '/account/edit-profile' },
        subscriptionOption,
        {
          name: 'Saved Guides',
          path: '/saved-guides',
        },
        purchasedGuidesOption,
        helpOption,
        requestFeatureOption,
        { name: 'Sign Out', onClick: handleSignOut },
      ].filter(link => link),
    },
  ]
}

export default () => {
  const [{ currentUser }] = useCurrentUser()
  const { width } = useWindowDimensions()
  const signedInLinks = useSignedInLinks()
  const linksToNonSignedUsers = useLinksToNonSignedUsers()

  const navigationLinks =
    width < 960
      ? [
          {
            name: 'Guides',
            links: [
              { name: 'Search', path: '/search' },
              { name: 'All Study Guides', path: '/all-study-guides' },
              { name: 'Browse Alphabetically', path: '/browse-by-title' },
              { name: 'Classic Fiction', path: '/classic-fiction' },
              { name: 'Modern Fiction', path: '/modern-fiction' },
              { name: 'Nonfiction', path: '/non-fiction' },
              { name: 'Plays', path: '/plays' },
              { name: 'Short Stories', path: '/short-stories' },
              { name: 'Essays', path: '/essays' },
              { name: 'Poetry', path: '/poetry' },
              { name: 'Middle Grade/YA', path: '/middle-grade-ya' },
            ],
          },
          ...signedInLinks,
        ]
      : []

  if (currentUser) {
    return navigationLinks
  }
  return [...linksToNonSignedUsers, ...navigationLinks]
}
